import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X as XIcon } from "lucide-react";
import { listGoalCategories, createGoal, getGoal, updateGoal } from "@/lib/api";
import { FREQUENCIES, DAY_NAMES, TIME_PRESETS } from "./constants";
import { fmtTime, fmtYMD, legacyRhythmConfig, DATE_QUICK_CHIPS } from "./helpers";

export default function GoalComposer({ editingId = null }) {
    const nav = useNavigate();
    const [categories, setCategories] = useState([]);
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("");
    const [theme, setTheme] = useState("");
    const [why, setWhy] = useState("");
    const [targetDate, setTargetDate] = useState("");
    const [frequency, setFrequency] = useState("none");
    const [occasionDate, setOccasionDate] = useState("");
    const [occasionTime, setOccasionTime] = useState("");
    const [occasionLabel, setOccasionLabel] = useState("");
    const [times, setTimes] = useState([]);
    const [daysOfWeek, setDaysOfWeek] = useState([2, 4]);
    const [dayOfMonth, setDayOfMonth] = useState("1");
    const [remindersEnabled, setRemindersEnabled] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadedEditing, setLoadedEditing] = useState(!editingId);
    const [goalType, setGoalType] = useState("standard");
    const [qTarget, setQTarget] = useState(100);
    const [qUnit, setQUnit] = useState("oz");
    const [qIncrement, setQIncrement] = useState(8);
    const [qIcon, setQIcon] = useState("water");

    useEffect(() => {
        listGoalCategories().then(d => {
            setCategories(d.categories || []);
            if (!editingId && (d.categories || []).length && !category) {
                setCategory(d.categories[0].name);
            }
        });
    }, [category, editingId]);

    useEffect(() => {
        if (!editingId) return;
        getGoal(editingId).then(d => {
            const g = d.goal;
            if (!g) return;
            setTitle(g.title || "");
            setCategory(g.category || "");
            setTheme(g.theme || "");
            setWhy(g.why || "");
            setTargetDate(g.target_date || "");
            const cfg = legacyRhythmConfig(g) || { frequency: "none", times: [] };
            setFrequency(cfg.frequency || "none");
            setTimes(cfg.times || []);
            setDaysOfWeek(cfg.days_of_week || [2, 4]);
            setDayOfMonth(cfg.day_of_month != null ? String(cfg.day_of_month) : "1");
            setOccasionDate(cfg.occasion_date || "");
            const firstTime = (cfg.times || [])[0] || {};
            if (cfg.frequency === "once") {
                setOccasionTime(firstTime.time || "");
                setOccasionLabel(firstTime.label || "");
            }
            setRemindersEnabled(cfg.reminders_enabled !== false);
            setGoalType(g.goal_type || "standard");
            const qc = g.quantity_config || {};
            setQTarget(qc.target_per_day || 100);
            setQUnit(qc.unit || "oz");
            setQIncrement(qc.increment || 8);
            setQIcon(qc.icon || "water");
            setLoadedEditing(true);
        });
    }, [editingId]);

    const themesForCategory = useMemo(() => {
        const c = categories.find(x => x.name === category);
        return c ? c.themes : [];
    }, [categories, category]);

    useEffect(() => { if (!editingId) setTheme(""); }, [category, editingId]);

    const addTime = (preset) => {
        setTimes(prev => [...prev, { label: preset?.label || "", time: preset?.time || "12:00" }].slice(0, 4));
    };
    const updateTime = (idx, patch) => setTimes(prev => prev.map((t, i) => i === idx ? { ...t, ...patch } : t));
    const removeTime = (idx) => setTimes(prev => prev.filter((_, i) => i !== idx));

    const toggleDow = (d) => {
        setDaysOfWeek(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
    };

    const buildRhythmConfig = () => {
        if (frequency === "none") return { frequency, times: [], reminders_enabled: false };
        const cfg = { frequency, reminders_enabled: remindersEnabled, times };
        if (frequency === "once") {
            cfg.occasion_date = occasionDate;
            cfg.times = occasionTime ? [{ label: occasionLabel || "", time: occasionTime }] : [];
        }
        if (frequency === "weekly") cfg.days_of_week = daysOfWeek;
        if (frequency === "monthly") cfg.day_of_month = dayOfMonth;
        return cfg;
    };

    const canSave = title.trim() && category && !saving && (
        frequency !== "once" || (occasionDate && occasionTime)
    );

    const save = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const legacyRhythm = frequency === "once" ? "once" : frequency;
            const body = {
                title, category, theme, why, rhythm: legacyRhythm,
                target_date: targetDate || "",
                rhythm_config: buildRhythmConfig(),
                goal_type: goalType,
                quantity_config: goalType === "quantity"
                    ? { target_per_day: Number(qTarget) || 0, unit: qUnit, increment: Number(qIncrement) || 1, icon: qIcon }
                    : null,
            };
            if (editingId) {
                await updateGoal(editingId, body);
                nav(`/goals/${editingId}`);
            } else {
                const g = await createGoal(body);
                nav(`/goals/${g.id}`);
            }
        } finally { setSaving(false); }
    };

    return (
        <div
            data-testid={editingId ? "goal-editor" : "goal-composer"}
            className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full"
            style={{ maxWidth: "72ch" }}
        >
            <button
                onClick={() => nav(editingId ? `/goals/${editingId}` : "/goals")}
                className="inline-flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink mb-3"
            >
                <ArrowLeft size={14} /> {editingId ? "Back to goal" : "Goals"}
            </button>

            <h1 className="scripture text-3xl md:text-4xl text-ink tracking-tight mb-1">
                {editingId ? "Edit goal" : "New goal"}
            </h1>
            <p className="font-ui text-[13px] text-ink-70 mb-6">
                {editingId
                    ? "Refine the horizon or reshape the rhythm."
                    : <><span className="italic">"Write the vision. Make it plain."</span> <span className="text-ink-40">— Habakkuk 2:2</span> · What are you working toward?</>}
            </p>

            {editingId && !loadedEditing && (
                <p className="font-ui text-sm text-ink-40">Loading…</p>
            )}

            <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mb-1">Title</label>
            <input
                data-testid="goal-title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Run 3x a week"
                className="w-full px-4 py-3 rounded-2xl bg-white border border-ink-10 scripture text-[18px] focus:outline-none focus:border-moss/60"
                autoFocus
            />

            <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mt-5 mb-2">Category</label>
            <div className="flex flex-wrap gap-1.5">
                {categories.map(c => {
                    const on = category === c.name;
                    return (
                        <button
                            key={c.name}
                            data-testid={`cat-chip-${c.name}`}
                            onClick={() => setCategory(c.name)}
                            className={
                                "px-3 py-1.5 rounded-full font-ui text-[12px] transition " +
                                (on ? "bg-moss text-white" : "bg-white border border-ink-10 text-ink-70 hover:border-moss/40")
                            }
                        >
                            {c.name}
                        </button>
                    );
                })}
            </div>

            {themesForCategory.length > 0 && (
                <>
                    <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mt-5 mb-2">
                        Theme <span className="normal-case tracking-normal text-ink-40/70">(edit to fit — we'll infer if you leave it)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            onClick={() => setTheme("")}
                            className={
                                "px-3 py-1.5 rounded-full font-ui text-[12px] transition " +
                                (theme === "" ? "bg-ink text-white" : "bg-white border border-ink-10 text-ink-70 hover:border-moss/40")
                            }
                        >
                            Auto
                        </button>
                        {themesForCategory.map(t => (
                            <button
                                key={t}
                                data-testid={`theme-chip-${t}`}
                                onClick={() => setTheme(t)}
                                className={
                                    "px-3 py-1.5 rounded-full font-ui text-[12px] italic transition " +
                                    (theme === t
                                        ? "bg-moss text-white"
                                        : "bg-sage-paper text-ink-70 hover:bg-ink-10")
                                }
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </>
            )}

            <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mt-5 mb-1">Why (optional)</label>
            <textarea
                data-testid="goal-why"
                value={why}
                onChange={e => setWhy(e.target.value)}
                rows={3}
                placeholder="What is God stirring in you here? Anchor a verse with e.g. Mat.6.33 or Phil.4.13"
                className="w-full px-4 py-3 rounded-2xl bg-white border border-ink-10 scripture text-[15px] leading-relaxed focus:outline-none focus:border-moss/60 resize-none"
            />

            <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mt-5 mb-2">Goal type</label>
            <div className="flex flex-wrap gap-1.5">
                <button
                    data-testid="goal-type-standard"
                    onClick={() => setGoalType("standard")}
                    className={
                        "px-3 py-1.5 rounded-full font-ui text-[12px] transition " +
                        (goalType === "standard" ? "bg-moss text-white" : "bg-white border border-ink-10 text-ink-70 hover:border-moss/40")
                    }
                >
                    Standard
                </button>
                <button
                    data-testid="goal-type-quantity"
                    onClick={() => setGoalType("quantity")}
                    className={
                        "px-3 py-1.5 rounded-full font-ui text-[12px] transition " +
                        (goalType === "quantity" ? "bg-moss text-white" : "bg-white border border-ink-10 text-ink-70 hover:border-moss/40")
                    }
                >
                    Track a daily amount
                </button>
            </div>
            {goalType === "quantity" && (
                <div data-testid="quantity-fields" className="mt-3 flex flex-wrap items-center gap-2">
                    <label className="font-ui text-[11px] text-ink-40">Daily target</label>
                    <input
                        data-testid="q-target"
                        type="number"
                        min="1"
                        value={qTarget}
                        onChange={e => setQTarget(e.target.value)}
                        className="w-20 px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                    />
                    <input
                        data-testid="q-unit"
                        value={qUnit}
                        onChange={e => setQUnit(e.target.value)}
                        placeholder="unit"
                        className="w-16 px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                    />
                    <label className="font-ui text-[11px] text-ink-40 ml-2">per tap</label>
                    <input
                        data-testid="q-increment"
                        type="number"
                        min="1"
                        value={qIncrement}
                        onChange={e => setQIncrement(e.target.value)}
                        className="w-20 px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                    />
                    <div className="ml-2 flex gap-1.5">
                        {["water", "generic"].map(k => (
                            <button
                                key={k}
                                onClick={() => setQIcon(k)}
                                className={
                                    "px-2.5 py-1 rounded-full font-ui text-[11px] transition " +
                                    (qIcon === k ? "bg-moss text-white" : "bg-sage-paper text-ink-70 hover:bg-ink-10")
                                }
                            >
                                {k === "water" ? "💧 water" : "● generic"}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mt-5 mb-2">
                Target date <span className="normal-case tracking-normal text-ink-40/70">(optional — a gentle horizon)</span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
                <input
                    data-testid="target-date-input"
                    type="date"
                    value={targetDate}
                    onChange={e => setTargetDate(e.target.value)}
                    className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                />
                {targetDate && (
                    <button
                        data-testid="target-date-clear"
                        onClick={() => setTargetDate("")}
                        className="font-ui text-[11px] text-ink-40 hover:text-ink underline-offset-2 hover:underline"
                    >
                        Clear
                    </button>
                )}
                {DATE_QUICK_CHIPS().map(chip => (
                    <button
                        key={chip.key}
                        data-testid={`target-chip-${chip.key}`}
                        onClick={() => setTargetDate(chip.value)}
                        className={
                            "px-3 py-1.5 rounded-full font-ui text-[11px] transition " +
                            (targetDate === chip.value
                                ? "bg-moss text-white"
                                : "bg-sage-paper text-ink-70 hover:bg-ink-10")
                        }
                    >
                        {chip.label}
                    </button>
                ))}
            </div>

            <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mt-5 mb-2">How often?</label>
            <div className="flex flex-wrap gap-1.5">
                {FREQUENCIES.map(r => {
                    const on = frequency === r.key;
                    return (
                        <button
                            key={r.key}
                            data-testid={`freq-${r.key}`}
                            onClick={() => setFrequency(r.key)}
                            className={
                                "px-3 py-1.5 rounded-full font-ui text-[12px] transition " +
                                (on ? "bg-moss text-white" : "bg-white border border-ink-10 text-ink-70 hover:border-moss/40")
                            }
                        >
                            {r.label}
                        </button>
                    );
                })}
            </div>

            {frequency === "once" && (
                <div className="mt-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            data-testid="once-date"
                            type="date"
                            value={occasionDate}
                            onChange={e => setOccasionDate(e.target.value)}
                            className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                        />
                        <input
                            data-testid="once-time"
                            type="time"
                            value={occasionTime}
                            onChange={e => setOccasionTime(e.target.value)}
                            className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                        />
                        <input
                            data-testid="once-label"
                            value={occasionLabel}
                            onChange={e => setOccasionLabel(e.target.value)}
                            placeholder="Label (optional)"
                            className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] w-40 focus:outline-none focus:border-moss/60"
                        />
                    </div>
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                        <button onClick={() => setOccasionDate(fmtYMD(new Date()))} className="px-2.5 py-1 rounded-full bg-sage-paper text-ink-70 hover:bg-ink-10">Today</button>
                        <button onClick={() => { const t = new Date(); t.setDate(t.getDate() + 1); setOccasionDate(fmtYMD(t)); }} className="px-2.5 py-1 rounded-full bg-sage-paper text-ink-70 hover:bg-ink-10">Tomorrow</button>
                        {targetDate && (
                            <button onClick={() => setOccasionDate(targetDate)} className="px-2.5 py-1 rounded-full bg-sage-paper text-ink-70 hover:bg-ink-10">Same day as horizon</button>
                        )}
                    </div>
                </div>
            )}

            {frequency === "weekly" && (
                <div className="mt-4">
                    <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mb-2">Days of week</div>
                    <div className="flex flex-wrap gap-1.5">
                        {DAY_NAMES.map((d, i) => {
                            const on = daysOfWeek.includes(i);
                            return (
                                <button
                                    key={i}
                                    data-testid={`dow-${i}`}
                                    onClick={() => toggleDow(i)}
                                    className={
                                        "w-11 h-9 rounded-full font-ui text-[12px] transition " +
                                        (on ? "bg-moss text-white" : "bg-white border border-ink-10 text-ink-70 hover:border-moss/40")
                                    }
                                >
                                    {d}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {frequency === "monthly" && (
                <div className="mt-4">
                    <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mb-2">Which day of the month?</div>
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            data-testid="dom"
                            value={dayOfMonth}
                            onChange={e => setDayOfMonth(e.target.value)}
                            className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                        >
                            <optgroup label="A specific date">
                                {Array.from({ length: 31 }, (_, i) => i + 1).map(n => (
                                    <option key={n} value={String(n)}>{n}{n === 31 ? "st (Feb → 28/29)" : ""}</option>
                                ))}
                                <option value="last_day">Last day</option>
                            </optgroup>
                            <optgroup label="A pattern">
                                {["first", "second", "third", "fourth", "last"].flatMap(ord =>
                                    ["SUN","MON","TUE","WED","THU","FRI","SAT"].map(d =>
                                        <option key={`${ord}_${d}`} value={`${ord}_${d}`}>{`${ord[0].toUpperCase()+ord.slice(1)} ${DAY_NAMES[["SUN","MON","TUE","WED","THU","FRI","SAT"].indexOf(d)]}`}</option>
                                    )
                                )}
                            </optgroup>
                        </select>
                    </div>
                </div>
            )}

            {(frequency === "daily" || frequency === "weekly" || frequency === "monthly") && (
                <div className="mt-4">
                    <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mb-2">
                        At what time(s)? <span className="normal-case tracking-normal text-ink-40/70 italic ml-1">"Evening, and morning, and at noon" — Psalm 55:17</span>
                    </div>
                    {times.length === 0 && (
                        <p className="font-ui text-[12px] italic text-ink-40 mb-2">
                            No times yet — check-ins will be manual. Add one below to schedule slot markers.
                        </p>
                    )}
                    {times.map((t, i) => (
                        <div key={i} data-testid={`time-row-${i}`} className="flex flex-wrap items-center gap-2 mb-2">
                            <input
                                type="time"
                                value={t.time}
                                onChange={e => updateTime(i, { time: e.target.value })}
                                className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                            />
                            <input
                                value={t.label}
                                onChange={e => updateTime(i, { label: e.target.value })}
                                placeholder='Label (e.g. "Before work")'
                                className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] w-56 focus:outline-none focus:border-moss/60"
                            />
                            <button
                                data-testid={`time-remove-${i}`}
                                onClick={() => removeTime(i)}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-ink-40 hover:text-plum hover:bg-plum/10 transition"
                            >
                                <XIcon size={13} />
                            </button>
                        </div>
                    ))}
                    <div className="flex flex-wrap gap-1.5 text-[11px] mt-2">
                        {TIME_PRESETS.map(p => (
                            <button
                                key={p.key}
                                data-testid={`time-preset-${p.key}`}
                                disabled={times.length >= 4}
                                onClick={() => addTime(p)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sage-paper text-ink-70 hover:bg-ink-10 disabled:opacity-40"
                            >
                                <p.icon size={11} /> {p.label} ({fmtTime(p.time)})
                            </button>
                        ))}
                        <button
                            data-testid="time-add"
                            disabled={times.length >= 4}
                            onClick={() => addTime()}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-moss text-white hover:bg-moss-deep disabled:opacity-40"
                        >
                            <Plus size={11} /> Add time
                        </button>
                    </div>
                </div>
            )}

            {frequency !== "none" && (
                <label className="mt-4 inline-flex items-center gap-2 font-ui text-[12px] text-ink-70">
                    <input
                        data-testid="reminders-enabled"
                        type="checkbox"
                        checked={remindersEnabled}
                        onChange={e => setRemindersEnabled(e.target.checked)}
                        className="w-4 h-4 accent-moss"
                    />
                    Reminders enabled
                </label>
            )}

            <div className="mt-8 flex gap-2 justify-end">
                <button
                    onClick={() => nav(editingId ? `/goals/${editingId}` : "/goals")}
                    className="px-4 py-2.5 rounded-full font-ui text-[13px] text-ink-70 hover:bg-ink-10"
                >
                    Cancel
                </button>
                <button
                    data-testid="goal-save"
                    disabled={!canSave}
                    onClick={save}
                    className={
                        "px-5 py-2.5 rounded-full font-ui text-[13px] font-medium transition " +
                        (canSave ? "bg-moss text-white hover:bg-moss-deep" : "bg-ink-10 text-ink-40 cursor-not-allowed")
                    }
                >
                    {editingId ? "Save changes" : "Write it down"}
                </button>
            </div>
        </div>
    );
}
